/**
 * Notification channel preference toggles.
 * Renders in the NotificationCenter dropdown footer.
 */

import { useCallback, useState } from 'react';
import { useNotifications } from '@/hooks/useNotifications';

export function NotificationPreferences() {
  const { preferences, updatePreferences } = useNotifications();
  const [error, setError] = useState<string | null>(null);

  const handleToggle = useCallback(
    async (field: 'slack_enabled' | 'in_app_enabled') => {
      if (!preferences) return;

      const newValue = !preferences[field];
      // Optimistic update handled via context

      try {
        setError(null);
        await updatePreferences({ [field]: newValue });
      } catch {
        setError('Failed to update');
      }
    },
    [preferences, updatePreferences]
  );

  if (!preferences) return null;

  return (
    <div className="border-t border-dark-700 px-4 py-3 space-y-2">
      <p className="text-xs font-medium text-dark-400 uppercase tracking-wider mb-2">
        Delivery Channels
      </p>

      <ToggleRow
        label="Slack"
        enabled={preferences.slack_enabled}
        onToggle={() => handleToggle('slack_enabled')}
      />
      <ToggleRow
        label="In-app"
        enabled={preferences.in_app_enabled}
        onToggle={() => handleToggle('in_app_enabled')}
      />

      {error && (
        <p className="text-xs text-rose-400 mt-1">{error}</p>
      )}
    </div>
  );
}

function ToggleRow({
  label,
  enabled,
  onToggle,
}: {
  label: string;
  enabled: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-dark-300">{label}</span>
      <button
        onClick={onToggle}
        role="switch"
        aria-checked={enabled}
        aria-label={`${label} notifications ${enabled ? 'enabled' : 'disabled'}`}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
          enabled ? 'bg-primary-600' : 'bg-dark-600'
        }`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  );
}
